import React, { useState, useEffect, useRef } from "react";
import "../About/AboutUs.css";
import PageLoader from "../Assets/PageLoader/PageLoader";
import { callMultipleApis } from "../../services/ApiServices";

const submitContact = process.env.REACT_APP_CONTACTFORM_URL;
const getCaptchaKey = process.env.REACT_APP_CAPTCHAKEY_URL;

const AboutUs = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [loading, setLoading] = useState(true);
  const isMountedRef = useRef(false);
  const ref = useRef(null);
  const widgetIdRef = useRef(null);
  const tokenRef = useRef(null);
  const pendingSubmitRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;

    const initTurnstile = async () => {
      if (!window.turnstile || !ref.current) return;

      setLoading(true);

      try {
        const [resKey] = await callMultipleApis([
          {
            url: getCaptchaKey,
            method: "POST",
            data: {},
            params: {},
            headers: {},
          }
        ]);

        if (!isMountedRef.current) return;

        // ✅ Remove old widget if any
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.remove(widgetIdRef.current);
        }

        // ✅ Always reset ref before creating new widget
        widgetIdRef.current = null;

        // ✅ Render captcha
        widgetIdRef.current = window.turnstile.render(ref.current, {
          sitekey: resKey.result,
          callback: (token) => {
            tokenRef.current = token;
            if (pendingSubmitRef.current) {
              pendingSubmitRef.current = false;
              postContactForm(token);
            }
          },
          "error-callback": () => (tokenRef.current = null),
        });
      } catch (err) {
        console.error("Turnstile error", err);
      } finally {
        setLoading(false);
      }
    };

    initTurnstile();

    return () => {
      isMountedRef.current = false;
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, []);


  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!tokenRef.current) {
      //alert("Captcha not verified");
      pendingSubmitRef.current = true;
      // 🔁 Silent refresh
      window.turnstile.reset(widgetIdRef.current);
      return;
    }

    postContactForm(tokenRef.current);

  };


  const postContactForm = async (token) => {
    try {
      setLoading(true);
      const data = { name: formData.name, email: formData.email, message: formData.message, captchaToken: token }
      const [resContactForm] = await callMultipleApis([
        {
          url: submitContact,
          method: "POST",
          data: data,
          params: {},
          headers: {},
        }
      ]);

      if (resContactForm.status === true) {
        alert("Message submitted successfully!");
        setFormData({ name: "", email: "", message: "" });
      } else {
        alert(resContactForm.message);
      }

    } catch (error) {

    } finally {
      setLoading(false);
    }

  }


  return (
    <div className="about-container">
      <h1 className="about-title">About Us</h1>
      <p className="about-tagline">"Crafting Happiness with Every Product."</p>

      <section className="about-section">
        <h2>Our Story</h2>
        <p>
          Kanha WonderCraft is a vibrant and customer-focused startup
          specializing in the reselling of toys, gift items, and Kanha dresses
          and accessories. We bring charm to celebrations through a diverse
          range of high-quality, thoughtfully curated products — combining
          cultural authenticity with modern appeal.
        </p>
      </section>

      <section className="about-section">
        <h2>Vision & Mission</h2>
        <p>
          <strong>Vision:</strong> To become a trusted brand that enhances
          life's special moments with unique offerings.
        </p>
        <p>
          <strong>Mission:</strong>
        </p>
        <ul>
          <li>Deliver high-quality, affordable, and unique products.</li>
          <li>
            Promote cultural heritage through traditional Kanha accessories.
          </li>
          <li>Provide outstanding customer service.</li>
          <li>Encourage sustainability by sourcing eco-friendly products.</li>
        </ul>
      </section>

      <section className="about-section">
        <h2>Our Products</h2>
        <ul>
          <li>
            <strong>Toys:</strong> Safe, creative, and educational toys for all
            age groups.
          </li>
          <li>
            <strong>Gift Items:</strong> Unique and customizable gifts for all
            occasions.
          </li>
          <li>
            <strong>Kanha Dresses:</strong> Handcrafted traditional attire and
            accessories for Lord Krishna.
          </li>
        </ul>
      </section>

      <section className="about-section">
        <h2>Key Highlights</h2>
        <ul>
          <li>Customer-Centric Approach</li>
          <li>Strict Quality Assurance</li>
          <li>Affordable Pricing</li>
          <li>Wide Product Variety</li>
        </ul>
      </section>

      <section className="about-section">
        <h2>Future Goals</h2>
        <ul>
          <li>Expand product lines with theme-based toys and gifts.</li>
          <li>Promote Indian culture globally through Kanha accessories.</li>
          <li>Open retail stores in major cities.</li>
          <li>Adopt eco-friendly practices across all products.</li>
        </ul>
      </section>


      <div className="about-footer">
        At Kanha WonderCraft, we are passionate about creating unforgettable
        experiences. Feel free to reach out if you have any query.
      </div>

      <section className="about-section">
        <div className="contact-container">
          <h2 className="contact-title">Contact Us</h2>
          <form className="contact-form" onSubmit={handleSubmit}>
            <label>
              <span>Name <span className="required">*</span></span>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </label>

            <label>
              <span>Email <span className="required">*</span></span>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </label>

            <label>
              <span> Message <span className="required">*</span></span>
              <textarea
                name="message"
                rows="4"
                value={formData.message}
                onChange={handleChange}
                required
              ></textarea>
            </label>

            <div ref={ref}></div>

            <button type="submit">Send Message</button>
          </form>
        </div>
      </section>

      {loading && (
        <PageLoader text="" />
      )}


    </div>
  );
}

export default AboutUs;
